import { LIS_NEW, LIS_DRAFT, LIW_NEW_AM, LIT_CHANGE, FILE_TRACKING_PIXEL } from '../constant';
import { Creative } from '../model/creative';
import { Note } from '../model/note';
import { Kpi } from '../model/kpi';
import LineItemProductType from '../model/line-item-product-type';
import LineItemTargeting from '../model/line-item-targeting';
import { TrackingPixel } from '../model/tracking-pixel';
import { Headline } from '../model/headline';
import { CompanionAsset } from '../model/companion-asset';
import { File } from '../model/file';
import { uuid, isBlank } from './common';

export default {
  /**
   *@param {Boolean} isPlanIdNull when true, the new line item will be the 'planId = null'
   */
  clonePlan(
    plan,
    isPlanIdNull,
    orderStatusId,
    lineItemStatusId,
    orderFlagId,
    lineItemFlagId,
    resetFlight
  ) {
    const clonedPlan = plan.inertClone();

    clonedPlan.flag = false;

    // null some other fields which should not be duped
    clonedPlan._uuid = uuid();
    clonedPlan.id = null;
    clonedPlan.updatedPlan = true;
    clonedPlan.show = false;
    clonedPlan.statusId = orderStatusId;
    clonedPlan.crPlans = [];
    clonedPlan.flagId = orderFlagId;

    clonedPlan.lineItems = plan.lineItems
      .filter((li) => li.type !== LIT_CHANGE)
      .map((li) =>
        this.cloneLineItem(li, false, isPlanIdNull, lineItemStatusId, lineItemFlagId, resetFlight)
      );

    clonedPlan.files = plan.files.map((at) =>
      Object.assign(new File(), at, { _uuid: uuid(), id: null })
    );

    if (plan.note) {
      // Exixting plan may have empty note objects
      if (plan.note.isEmpty()) {
        clonedPlan.note = null;
      } else {
        clonedPlan.note = Object.assign(new Note(), plan.note, { _uuid: uuid(), id: null });
        clonedPlan.note.files = plan.note.files.map((file) =>
          Object.assign(new File(), file, { _uuid: uuid(), id: null })
        );
      }
    }

    return clonedPlan;
  },

  /**
   * Clone the given LineItem. When `flighting = true`:
   * - adreadyId is copied also
   * - flightStartDate and flightEndDate are made null
   *
   * @param {LineItem} li Line Item to clone
   * @param {Boolean} flighting When true, the new line item will be a 'flight' of the existing one.
   * @param {Boolean} isPlanIdNull when true, the new line item will be the 'planId = null'
   * @param {Number} lineItemStatusId Status to set for the new Line Item
   * @param {Number} lineItemFlagId flag to set for the new Line Item
   */
  cloneLineItem(li, flighting, isPlanIdNull, lineItemStatusId, lineItemFlagId, resetFlight) {
    const clonedLineItem = li.inertClone();

    clonedLineItem.crLineItems = [];
    // reset status, assignee
    clonedLineItem.statusId = lineItemStatusId;
    if (lineItemStatusId === LIS_NEW || lineItemStatusId === LIS_DRAFT) {
      clonedLineItem.workflowId = LIW_NEW_AM;
    }
    clonedLineItem._isWorkflowChanged = true;
    clonedLineItem.flagId = lineItemFlagId;

    // reset workflow stuff on clone a lineItem.
    clonedLineItem.assignedTo = null;
    clonedLineItem.qa = null;
    clonedLineItem.taskTypeId = null;
    clonedLineItem.flag = false;

    // null some other fields which should not be duped
    clonedLineItem._uuid = uuid();
    clonedLineItem.id = null;
    if (isPlanIdNull) {
      clonedLineItem.planId = null;
    }

    if (resetFlight) {
      clonedLineItem.flightStartDate = null;
      clonedLineItem.flightEndDate = null;
    }

    if (flighting) {
      clonedLineItem.creatives = li.creatives.map((creative) =>
        Object.assign(new Creative(), creative, {
          _uuid: uuid(),
          id: null,
          creativeEndDate: null,
          creativeStartDate: null,
        })
      );
    } else {
      clonedLineItem.adreadyId = null;
      clonedLineItem.creatives = li.creatives.map((creative) =>
        Object.assign(new Creative(), creative, { _uuid: uuid(), id: null })
      );
    }

    clonedLineItem.creatives.forEach((creative) => {
      creative.files = creative.files.map((file) =>
        Object.assign(new File(), file, { _uuid: uuid(), id: null })
      );
      creative.headlines = creative.headlines.map((h) =>
        Object.assign(new Headline(), h, { _uuid: uuid(), id: null })
      );
      creative.companionAssets = creative.companionAssets.map((ca) =>
        Object.assign(new CompanionAsset(), ca, { _uuid: uuid(), id: null, images: null })
      );
    });

    if (li.note) {
      // Exixting lineItem may have empty note objects
      if (li.note.isEmpty()) {
        clonedLineItem.note = null;
      } else {
        clonedLineItem.note = Object.assign(new Note(), li.note, { _uuid: uuid(), id: null });
        clonedLineItem.note.files = li.note.files.map((file) =>
          Object.assign(new File(), file, { _uuid: uuid(), id: null })
        );
      }
    }

    clonedLineItem.externalName = null;
    // clonedLineItem.name = null;
    clonedLineItem.updatedLineItem = true;

    clonedLineItem.kpis = li.kpis.map((kpi) =>
      Object.assign(new Kpi(), kpi, { _uuid: uuid(), id: null })
    );

    clonedLineItem.productTypes = li.productTypes.map((pt) =>
      Object.assign(new LineItemProductType(), pt, { _uuid: uuid(), id: null })
    );

    clonedLineItem.targetings = li.targetings.map((t) =>
      Object.assign(new LineItemTargeting(), t, { _uuid: uuid(), id: null })
    );

    clonedLineItem.deviceTypes = li.deviceTypes.map((deviceType) => ({ ...deviceType, id: null }));
    clonedLineItem.vendors = li.vendors.map((v) => ({ ...v, id: null }));
    clonedLineItem.trackingPixels = li.trackingPixels.map((trackingPixel) =>
      Object.assign(new TrackingPixel(), trackingPixel, { _uuid: uuid(), idKey: null })
    );
    // tracking pixels files id reset
    clonedLineItem.trackingPixels.forEach((trackingPixel) => {
      if (trackingPixel.id === FILE_TRACKING_PIXEL) {
        trackingPixel.files = trackingPixel.files.map((file) =>
          Object.assign(new File(), file, { _uuid: uuid(), id: null })
        );
      }
    });
    return clonedLineItem;
  },

  /**
   * Merge the CR LineItem(crLi) into its parent(li).
   *
   * @param {LineItem} li Line Item is parent LineItem
   * @param {LineItem} crLi Line Item is Change Request LineItem, need to merge to its parent(li)
   * @param {LineItem} if the merge of lineItem is for Change Request Validation purpose only then its value is true.
   *
   */
  mergeLineItem(li, crLi, isForCRValidation) {
    const inertLi = li.inertClone();

    if(!isBlank(crLi.name) && !isBlank(crLi.name.trim())) {
        inertLi.name = crLi.name;
    }
    if (crLi.assignedTo !== null) {
      inertLi.assignedTo = crLi.assignedTo;
    }

    if (crLi.billingType !== null) {
      inertLi.billingType = crLi.billingType;
    }

    if (crLi.contactUserId !== null) {
      inertLi.contactUserId = crLi.contactUserId;
    }

    if (crLi.customUserId !== null) {
      inertLi.customUserId = crLi.customUserId;
    }

    if (crLi.fixCpm !== 0) {
      inertLi.fixCpm = crLi.fixCpm;
    }

    if (crLi.lineItemBudget !== 0) {
      inertLi.lineItemBudget = crLi.lineItemBudget;
    }

    if (crLi.mediaSpend !== 0) {
      inertLi.mediaSpend = crLi.mediaSpend;
    }

    if (crLi.price !== null) {
      inertLi.price = crLi.price;
    }
    if (crLi.audienceName !== null) {
      inertLi.audienceName = crLi.audienceName;
    }

    if (crLi.pricingType !== null) {
      inertLi.pricingType = crLi.pricingType;
    }

    if (crLi.mediaData !== null) {
        inertLi.mediaData = crLi.mediaData;
    }

    if (crLi.impressions !== null) {
      inertLi.impressions = crLi.impressions;
    }

    if (crLi.managementFeeOnly !== null) {
      inertLi.managementFeeOnly = crLi.managementFeeOnly;
    }

    if (crLi.productCategoryId !== null) {
      inertLi.productCategoryId = crLi.productCategoryId;
    }

    if (crLi.retargetingNotes !== null) {
      inertLi.retargetingNotes = crLi.retargetingNotes;
    }

    if (crLi.frequency !== null) {
      inertLi.frequency = crLi.frequency;
    }
    if (crLi.frequencyRecency !== null) {
      inertLi.frequencyRecency = crLi.frequencyRecency;
    }

    if (crLi.dayParting !== null) {
      inertLi.dayParting = crLi.dayParting;
    }

    // if (crLi.flip !== inertLi.flip) {
    //   inertLi.flip = crLi.flip;
    // }

    if (crLi.siteRetargeting !== null) {
      inertLi.siteRetargeting = crLi.siteRetargeting;
    }

    if (crLi.flightStartDate !== null) {
      inertLi.flightStartDate = crLi.flightStartDate;
    }
    if (crLi.flightEndDate !== null) {
      inertLi.flightEndDate = crLi.flightEndDate;
    }
    if (!isBlank(crLi.creatives)) {
      inertLi.creatives = crLi.creatives.map((creative) =>
        Object.assign(new Creative(), creative, { _uuid: uuid(), id: null })
      );
      inertLi.creatives.forEach((creative) => {
        creative.files = creative.files.map((file) =>
          Object.assign(new File(), file, { _uuid: uuid(), id: null })
        );
      });
    }

    if (!isBlank(crLi.note) && !crLi.note.isEmpty()) {
      inertLi.note = Object.assign(new Note(), crLi.note, { _uuid: uuid(), id: null });
      inertLi.note.files = crLi.note.files.map((file) =>
        Object.assign(new File(), file, { _uuid: uuid(), id: null })
      );
    }

    if (!isBlank(crLi.kpis)) {
      inertLi.kpis = crLi.kpis.map((kpi) =>
        Object.assign(new Kpi(), kpi, { _uuid: uuid(), id: null })
      );
    }

    if (!isBlank(crLi.productTypes)) {
      inertLi.productTypes = crLi.productTypes.map((pt) =>
        Object.assign(new LineItemProductType(), pt, { _uuid: uuid(), id: null })
      );
    } else if (isForCRValidation && !isBlank(crLi.productCategoryId)) {
      inertLi.productTypes = [];
      inertLi.targetings = [];
    }

    if (!isBlank(crLi.targetings)) {
      inertLi.targetings = crLi.targetings.map((t) =>
        Object.assign(new LineItemTargeting(), t, { _uuid: uuid(), id: null })
      );
    }

    if (!isBlank(crLi.deviceTypes)) {
      inertLi.deviceTypes = crLi.deviceTypes.map((deviceType) => ({
        ...deviceType,
        id: null,
      }));
    }

    if (!isBlank(crLi.deviceTypes)) {
      inertLi.deviceTypes = crLi.deviceTypes.map((deviceType) => ({
        ...deviceType,
        id: null,
      }));
    }

    if (!isBlank(crLi.trackingPixels)) {
      inertLi.trackingPixels = crLi.trackingPixels.map((trackingPixel) =>
        Object.assign(new TrackingPixel(), trackingPixel, { _uuid: uuid(), idKey: null })
      );
      // tracking pixels files id reset
      inertLi.trackingPixels.forEach((trackingPixel) => {
        if (trackingPixel.id === FILE_TRACKING_PIXEL) {
          trackingPixel.files = trackingPixel.files.map((file) =>
            Object.assign(new File(), file, { _uuid: uuid(), id: null })
          );
        }
      });
    }
    inertLi.updatedLineItem = true;
    return inertLi;
  },
  mergePlan(plan, crPlan) {
    const inertPlan = plan.inertClone();
    if (!isBlank(crPlan.campaignName)) {
      inertPlan.campaignName = crPlan.campaignName;
    }
    if (!isBlank(crPlan.flightStartDate)) {
      inertPlan.flightStartDate = crPlan.flightStartDate;
    }
    if (!isBlank(crPlan.flightEndDate)) {
      inertPlan.flightEndDate = crPlan.flightEndDate;
    }
    if (!isBlank(crPlan.ownerId)) {
      inertPlan.ownerId = crPlan.ownerId;
    }
    if (!isBlank(crPlan.flipCampaignInfo)) {
      inertPlan.flipCampaignInfo = crPlan.flipCampaignInfo;
    }
    if (!isBlank(crPlan.lucidCampaignInfo)) {
      inertPlan.lucidCampaignInfo = crPlan.lucidCampaignInfo;
    }
    if (!isBlank(crPlan.brandSafetyLevelId)) {
      inertPlan.brandSafetyLevelId = crPlan.brandSafetyLevelId;
    }
    if (!isBlank(crPlan.verificationPartners)) {
      inertPlan.verificationPartners = crPlan.verificationPartners;
    }
    if (!isBlank(crPlan.note) && !crPlan.note.isEmpty()) {
      inertPlan.note = Object.assign(new Note(), crPlan.note, { _uuid: uuid(), id: null });
      inertPlan.note.files = crPlan.note.files.map((file) =>
        Object.assign(new File(), file, { _uuid: uuid(), id: null })
      );
    }
    if (!isBlank(crPlan.files)) {
      inertPlan.files = crPlan.files.map((file) =>
        Object.assign(new File(), file, { _uuid: uuid(), id: null })
      );
    }
    inertPlan.updatePlan = true;
    return inertPlan;
  },
};
